<template>
  <div
    class="min-h-screen bg-gray-900 flex flex-col sm:justify-start md:justify-start lg:justify-start pt-4 sm:pt-8 md:pt-12 sm:px-6 lg:px-8 overflow-y-auto"
  >
    <!-- Registration successful - verification required -->
    <div v-if="registrationSuccess" class="sm:mx-auto sm:w-full sm:max-w-md">
      <div
        class="bg-gray-800 py-8 px-6 shadow-md sm:rounded-lg sm:px-10 border border-gray-700"
      >
        <div class="text-center">
          <div class="text-green-400 text-6xl mb-4">📧</div>
          <h2 class="text-2xl font-extrabold text-white mb-2">
            Registracija uspešna!
          </h2>
          <p class="text-gray-400 mb-4">{{ registrationMessage }}</p>
          <p class="text-sm text-gray-500 mb-6">
            Proverite vašu email adresu
            <strong class="text-white">{{ userEmail }}</strong>
            i kliknite na link za verifikaciju da biste aktivirali vaš nalog.
          </p>

          <div class="space-y-4">
            <div
              class="bg-blue-900 border border-blue-700 text-blue-300 px-3 py-2 rounded text-sm"
            >
              <p class="mb-2">💡 <strong>Saveti:</strong></p>
              <ul class="text-left text-xs space-y-1">
                <li>• Proverite i spam/junk folder</li>
                <li>• Email može da stigne za nekoliko minuta</li>
                <li>• Link za verifikaciju važi 24 sata</li>
              </ul>
            </div>

            <button
              @click="resendVerification"
              :disabled="resendingVerification"
              class="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {{ resendingVerification ? 'Šalje se...' : 'Pošaljite ponovo' }}
            </button>

            <router-link
              to="/login"
              class="block w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-center"
            >
              Idite na prijavu
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Registration form -->
    <div v-else>
      <div class="sm:mx-auto sm:w-full sm:max-w-xl lg:max-w-2xl">
        <h2
          class="text-center text-2xl md:text-3xl font-extrabold text-white px-10"
        >
          Kreiraj novi nalog
        </h2>
        <p class="mt-2 text-center text-sm text-gray-400">
          Ili
          <router-link
            to="/login"
            class="font-medium text-indigo-400 hover:text-indigo-300"
          >
            prijavi se ako već imaš nalog
          </router-link>
        </p>
      </div>

      <div
        class="mt-3 sm:mx-auto sm:w-full sm:max-w-md md:max-w-lg lg:max-w-xl"
      >
        <div class="bg-gray-800 py-6 px-4 sm:rounded-lg sm:px-8">
          <RegisterForm
            @register-success="onRegisterSuccess"
            @show-toast="handleShowToast"
          />

          <!-- Privacy Policy and Terms Links -->
          <div class="mt-6 pt-4 border-t border-gray-700">
            <p class="text-xs text-gray-400 text-center mb-3">
              Registracijom se slažete sa našom politikom privatnosti
            </p>
            <div
              class="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4"
            >
              <router-link
                to="/privacy-policy"
                class="text-xs text-indigo-400 hover:text-indigo-300 underline"
              >
                📄 Politika privatnosti
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer with additional links -->
    <div class="mt-auto py-6">
      <div class="max-w-md mx-auto text-center">
        <div class="flex justify-center items-center space-x-6">
          <router-link
            to="/privacy-policy"
            class="text-xs text-gray-500 hover:text-gray-400 transition-colors"
          >
            Privatnost
          </router-link>
          <span class="text-gray-600">•</span>
          <router-link
            to="/contact"
            class="text-xs text-gray-500 hover:text-gray-400 transition-colors"
          >
            Kontakt
          </router-link>
        </div>
        <p class="text-xs text-gray-600 mt-2">
          © 2025 Moja Garderoba. Sva prava zadržana.
        </p>
      </div>
    </div>

    <!-- Toast notifikacija -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :title="toast.title"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import RegisterForm from '@/components/auth/RegisterForm.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import api from '@/services/api';

interface Toast {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title?: string;
}

export default defineComponent({
  name: 'RegisterView',
  components: {
    RegisterForm,
    ToastNotification,
  },
  setup() {
    const router = useRouter();
    const store = useStore();

    const registrationSuccess = ref(false);
    const registrationMessage = ref('');
    const userEmail = ref('');
    const resendingVerification = ref(false);

    const toast = ref<Toast>({
      show: false,
      message: '',
      type: 'info',
    });

    const onRegisterSuccess = (data: any) => {
      console.log('Registracija uspešna!', data);

      if (data.requiresVerification) {
        // Show verification screen
        registrationSuccess.value = true;
        registrationMessage.value =
          'Registracija je uspešna. Proverite svoj e-mail kako biste verifikovali nalog.';
        userEmail.value = data.user?.email || '';

        // Don't set as logged in - user needs to verify first
        // store.dispatch('auth/setLoggedIn', false);
      } else {
        // Old flow - user is immediately logged in (if backend changes later)
        store.dispatch('auth/setLoggedIn', true);
        router.push('/');
      }
    };

    const resendVerification = async () => {
      if (!userEmail.value) return;

      resendingVerification.value = true;

      try {
        const response = await api.post('/api/auth/resend-verification', {
          email: userEmail.value,
        });

        if (response.data.status === 'success') {
          toast.value = {
            show: true,
            message:
              'Verifikacioni email je ponovo poslat. Proverite vašu email adresu.',
            type: 'success',
            title: 'Email poslat',
          };
        }
      } catch (error: any) {
        toast.value = {
          show: true,
          message:
            error.response?.data?.message || 'Greška pri slanju verifikacije',
          type: 'error',
          title: 'Greška',
        };
      } finally {
        resendingVerification.value = false;
      }
    };

    const handleShowToast = (toastData: {
      message: string;
      type: string;
      title?: string;
    }) => {
      toast.value = {
        show: true,
        message: toastData.message,
        type: toastData.type as 'success' | 'error' | 'info' | 'warning',
        title: toastData.title,
      };
    };

    return {
      onRegisterSuccess,
      registrationSuccess,
      registrationMessage,
      userEmail,
      resendingVerification,
      resendVerification,
      toast,
      handleShowToast,
    };
  },
});
</script>

<style>
html,
body {
  height: 100%;
}

/* Omogući scrollovanje na manjim uređajima kad je potrebno */
@media (max-height: 800px) {
  html,
  body {
    overflow-y: auto;
  }
}

/* Zadrži fixed poziciju na većim ekranima */
@media (min-height: 801px) {
  html,
  body {
    overflow-y: auto;
  }

  /* Prilagođavanje vertikalnog razmaka na različitim veličinama ekrana */
  .min-h-screen {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
}
</style>
